import { isEmptyObject, useHasSession } from "raci-react-library";
import { Dispatch, PropsWithChildren, SetStateAction, createContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { CONTEXT_FLOW_STATE_SESSION_KEY } from "../../constants";
import { PRE_FORM_PAGE_URL } from "../../routing/routes.config";

export interface FlowState {
  /** Has the member completed the flow */
  isCompleted: boolean;
  /** Has the members session timed out */
  hasTimedOut: boolean;
  /** Has the members session erred */
  hasError: boolean;
  /** Does the members session have a payment error */
  hasPaymentError?: boolean;
}

const DEFAULT_FLOW_STATE: FlowState = { isCompleted: false, hasTimedOut: false, hasError: false };

export const FlowStateContext = createContext<[FlowState, Dispatch<SetStateAction<FlowState>>] | undefined>(undefined);

export const FlowStateProvider = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const hasSession = useHasSession();
  const [flowState, setFlowState] = useState(() => {
    const storedFlowState = JSON.parse(sessionStorage.getItem(CONTEXT_FLOW_STATE_SESSION_KEY) ?? "{}") as FlowState;
    const isStoredFlowStateValid = !!storedFlowState && !isEmptyObject(storedFlowState);
    const useStoredFlowState = isStoredFlowStateValid && location.pathname.toLowerCase() !== PRE_FORM_PAGE_URL;
    return useStoredFlowState ? storedFlowState : DEFAULT_FLOW_STATE;
  });

  useEffect(() => {
    if (hasSession) {
      sessionStorage.setItem(CONTEXT_FLOW_STATE_SESSION_KEY, JSON.stringify(flowState));
    }
  }, [hasSession, flowState]);

  return <FlowStateContext.Provider value={[flowState, setFlowState]}>{children}</FlowStateContext.Provider>;
};

export default FlowStateProvider;
